/**
 * markdownToWhatsapp.js
 *
 * Convert "standard" Markdown (headers, **bold**, [text](url), lists, etc.)
 * into WhatsApp-flavored text formatting.
 *
 * WhatsApp only supports a limited subset of formatting:
 *   *bold*        -> single asterisks
 *   _italic_      -> single underscores
 *   ~strikethrough~
 *   ```monospace```
 *   • bullet lists (plain characters, no native markdown list support)
 *
 * It does NOT support:
 *   # Headers, ## Headers, etc.
 *   **bold** (double asterisk)
 *   [text](url) links
 *   Tables, horizontal rules, etc.
 */

// Placeholder characters unlikely to appear in real text, used to protect
// "already converted to bold" spans from being re-touched by the later
// single-asterisk -> italic fallback pass.
const BOLD_OPEN = '\u0001';
const BOLD_CLOSE = '\u0002';

export function markdownToWhatsapp(md: string) {
  if (!md || typeof md !== 'string') return '';

  let text = md.replace(/\r\n/g, '\n');

  // 1. Remove horizontal rules (---, ***, ___ on their own line)
  text = text.replace(/^\s*([-*_]){3,}\s*$/gm, '');

  // 2. Headers -> protected-bold placeholder
  text = text.replace(/^#{1,6}\s+(.*)$/gm, (m, content) => {
    return `${BOLD_OPEN}${content.trim()}${BOLD_CLOSE}`;
  });

  // 3. **bold** / __bold__ -> protected-bold placeholder
  text = text.replace(
    /\*\*(.+?)\*\*/g,
    (m, content) => `${BOLD_OPEN}${content}${BOLD_CLOSE}`
  );
  text = text.replace(
    /__(.+?)__/g,
    (m, content) => `${BOLD_OPEN}${content}${BOLD_CLOSE}`
  );

  // 4. Remaining single-asterisk italics *text* -> _text_
  //    (anything still using single * at this point is genuine MD italic,
  //    since real bold has already been pulled out as placeholders)
  text = text.replace(
    /(^|[^*])\*(?!\*)([^*\n]+?)\*(?!\*)/g,
    (m, pre, content) => {
      return `${pre}_${content.trim()}_`;
    }
  );

  // 5. Strikethrough: ~~text~~ -> ~text~
  text = text.replace(/~~(.+?)~~/g, '~$1~');

  // 6. Links: [label](url) -> "label\nurl" (or just url if label duplicates url)
  text = text.replace(
    /\[([^\]]*)\]\((https?:\/\/[^\s)]+)\)/g,
    (m, label, url) => {
      const cleanLabel = label.trim();
      if (!cleanLabel || cleanLabel === url) return url;
      return `${cleanLabel}\n${url}`;
    }
  );

  // 7. Bullet lists: "- item" / "* item" / "+ item" -> "• item"
  text = text.replace(
    /^(\s*)[-*+]\s+(.*)$/gm,
    (m, indent, content) => `${indent}• ${content}`
  );

  // 8. Swap placeholders back to real WhatsApp bold (*text*)
  text = text.split(BOLD_OPEN).join('*').split(BOLD_CLOSE).join('*');

  // 9. Collapse 3+ blank lines into max 2
  text = text.replace(/\n{3,}/g, '\n\n');

  // 10. Trim trailing whitespace per line and overall
  text = text
    .split('\n')
    .map((line) => line.replace(/\s+$/, ''))
    .join('\n')
    .trim();

  return text;
}
